export interface AttendanceCaptureConfig {
  allowedSources: string[];
  timezone: string;
  requireGeo: boolean;
  requireFaceEvidence: boolean;
  duplicateWindowSeconds: number;
  allowUntypedEvents: boolean;
  normalizeUntypedEvents: boolean;
  normalizeMistypedEvents: boolean;
}

export interface AttendanceTrackingConfig {
  trackable: boolean;
  trackingIntervalSeconds: number;
  requireOnlineConnection: boolean;
}

export interface AttendanceTurnPolicyConfig {
  enableAutoClose: boolean;
  autoCloseGraceMinutes?: number | null;
}

export interface AttendanceZonesConfig {
  allowedZoneIds: string[];
  allowOutOfZonePunch: boolean;
}

export interface AttendanceDeviceRulesConfig {
  requireRegisteredDevice: boolean;
  requireDeviceBinding: boolean;
}

export interface AttendanceSourceRuleConfig {
  timezone?: string;
  requireGeo?: boolean;
  requireFaceEvidence?: boolean;
  duplicateWindowSeconds?: number;
  allowUntypedEvents?: boolean;
  normalizeUntypedEvents?: boolean;
  normalizeMistypedEvents?: boolean;
  requireRegisteredDevice?: boolean;
  requireDeviceBinding?: boolean;
}

export interface AttendanceConfigRecord {
  attendanceConfigId: string;
  center?: string;
  scopeLevel?: 'companiesRecord' | 'orgUnit' | 'employee';
  companiesRecordId?: string | null;
  orgUnitId?: string | null;
  orgPath?: string | null;
  employeeId?: string | null;
  attendanceCapture: AttendanceCaptureConfig;
  tracking: AttendanceTrackingConfig;
  turnPolicy?: AttendanceTurnPolicyConfig;
  zones: AttendanceZonesConfig;
  deviceRules?: AttendanceDeviceRulesConfig;
  sourceRules?: Record<string, AttendanceSourceRuleConfig>;
  version?: number;
  updatedBy?: string;
  updatedAt?: string;
  isActive?: boolean;
}

export interface AttendanceSourceRuleFormValue {
  enabled: boolean;
  timezone: string;
  requireGeo: boolean;
  requireFaceEvidence: boolean;
  duplicateWindowSeconds: number | '';
  allowUntypedEvents: boolean;
  normalizeUntypedEvents: boolean;
  normalizeMistypedEvents: boolean;
  requireRegisteredDevice: boolean;
  requireDeviceBinding: boolean;
}

interface AttendanceBaseCaptureLike {
  timezone?: string;
  requireGeo?: boolean;
  requireFaceEvidence?: boolean;
  duplicateWindowSeconds?: number | '';
  allowUntypedEvents?: boolean;
  normalizeUntypedEvents?: boolean;
  normalizeMistypedEvents?: boolean;
}

export interface AttendanceConfigFormValue {
  enabled: boolean;
  allowedSources: string[];
  timezone: string;
  requireGeo: boolean;
  requireFaceEvidence: boolean;
  duplicateWindowSeconds: number | '';
  allowUntypedEvents: boolean;
  normalizeUntypedEvents: boolean;
  normalizeMistypedEvents: boolean;
  trackable: boolean;
  trackingIntervalSeconds: number | '';
  requireOnlineConnection: boolean;
  turnPolicy: {
    enableAutoClose: boolean;
    autoCloseGraceMinutes: number | '';
  };
  allowedZoneIds: string[];
  allowOutOfZonePunch: boolean;
  isActive: boolean;
  sourceRules: Record<string, AttendanceSourceRuleFormValue>;
}

export interface AttendanceZoneOption {
  zoneId: string;
  companiesRecordId: string;
  name: string;
  icon?: string;
  isActive?: boolean;
}

export interface AttendanceConfigScope {
  companiesRecordId?: string | null;
  orgUnitId?: string | null;
  employeeId?: string | null;
}

export type AttendanceConfigListResponse =
  | AttendanceConfigRecord[]
  | {
      data?: AttendanceConfigRecord[];
      items?: AttendanceConfigRecord[];
      results?: AttendanceConfigRecord[];
      attendanceConfigs?: AttendanceConfigRecord[];
    };

export type AttendanceZoneListResponse =
  | AttendanceZoneOption[]
  | {
      data?: AttendanceZoneOption[];
      items?: AttendanceZoneOption[];
      results?: AttendanceZoneOption[];
      zones?: AttendanceZoneOption[];
    };

export const ATTENDANCE_ALLOWED_SOURCE_OPTIONS = ['web', 'mobile', 'device', 'kiosk'] as const;
export const ATTENDANCE_TIMEZONE_OPTIONS = ['America/Cancun', 'America/Mexico_City', 'UTC'] as const;
export const ATTENDANCE_SOURCE_OVERRIDE_OPTIONS = ['web', 'mobile', 'device', 'kiosk'] as const;
export const ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS = [5, 10, 20, 40, 60, 300, 600, 1200] as const;

function normalizeTrackingIntervalSeconds(
  seconds: number | null | undefined,
  legacyMinutes?: number | null
): number {
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
    return seconds;
  }

  if (typeof legacyMinutes === 'number' && Number.isFinite(legacyMinutes) && legacyMinutes > 0) {
    return legacyMinutes * 60;
  }

  return 300;
}

export function formatAttendanceTrackingInterval(
  seconds: number | null | undefined,
  legacyMinutes?: number | null
): string {
  const normalizedValue = normalizeTrackingIntervalSeconds(seconds, legacyMinutes);
  if ((!seconds || seconds <= 0) && (!legacyMinutes || legacyMinutes <= 0)) {
    return '-';
  }

  return normalizedValue < 60
    ? `${normalizedValue} s`
    : `${normalizedValue / 60} min`;
}

export function extractAttendanceConfigs(response: AttendanceConfigListResponse | null | undefined): AttendanceConfigRecord[] {
  if (!response) {
    return [];
  }

  return Array.isArray(response)
    ? response
    : response.data || response.items || response.results || response.attendanceConfigs || [];
}

export function findAttendanceConfigForScope(
  response: AttendanceConfigListResponse | null | undefined,
  scope: AttendanceConfigScope
): AttendanceConfigRecord | null {
  const configs = extractAttendanceConfigs(response);

  if (scope.employeeId) {
    return configs.find(
      (config) => config.scopeLevel === 'employee' && config.employeeId === scope.employeeId
    ) || null;
  }

  if (scope.orgUnitId) {
    return configs.find(
      (config) => config.scopeLevel === 'orgUnit' && config.orgUnitId === scope.orgUnitId
    ) || null;
  }

  if (scope.companiesRecordId) {
    return configs.find(
      (config) =>
        config.scopeLevel === 'companiesRecord' &&
        config.companiesRecordId === scope.companiesRecordId
    ) || null;
  }

  return null;
}

export function extractAttendanceZones(response: AttendanceZoneListResponse | null | undefined): AttendanceZoneOption[] {
  if (!response) {
    return [];
  }

  return Array.isArray(response) ? response : response.data || response.items || response.results || response.zones || [];
}

export function createEmptyAttendanceSourceRuleForm(
  source: string,
  base?: AttendanceBaseCaptureLike
): AttendanceSourceRuleFormValue {
  return {
    enabled: false,
    timezone: base?.timezone || ATTENDANCE_TIMEZONE_OPTIONS[0],
    requireGeo: base?.requireGeo !== false,
    requireFaceEvidence: base?.requireFaceEvidence === true,
    duplicateWindowSeconds: typeof base?.duplicateWindowSeconds === 'number' ? base.duplicateWindowSeconds : 60,
    allowUntypedEvents: base?.allowUntypedEvents !== false,
    normalizeUntypedEvents: base?.normalizeUntypedEvents !== false,
    normalizeMistypedEvents: base?.normalizeMistypedEvents !== false,
    requireRegisteredDevice: source === 'device' || source === 'biometricDevice' ? false : false,
    requireDeviceBinding: source === 'device' || source === 'biometricDevice' ? false : false,
  };
}

export function createEmptyAttendanceConfigForm(): AttendanceConfigFormValue {
  const baseCapture = {
    timezone: ATTENDANCE_TIMEZONE_OPTIONS[0],
    requireGeo: true,
    requireFaceEvidence: false,
    duplicateWindowSeconds: 60,
    allowUntypedEvents: true,
    normalizeUntypedEvents: true,
    normalizeMistypedEvents: true,
  };

  return {
    enabled: false,
    allowedSources: [],
    timezone: baseCapture.timezone,
    requireGeo: baseCapture.requireGeo,
    requireFaceEvidence: baseCapture.requireFaceEvidence,
    duplicateWindowSeconds: baseCapture.duplicateWindowSeconds,
    allowUntypedEvents: baseCapture.allowUntypedEvents,
    normalizeUntypedEvents: baseCapture.normalizeUntypedEvents,
    normalizeMistypedEvents: baseCapture.normalizeMistypedEvents,
    trackable: false,
    trackingIntervalSeconds: 300,
    requireOnlineConnection: true,
    turnPolicy: {
      enableAutoClose: false,
      autoCloseGraceMinutes: '',
    },
    allowedZoneIds: [],
    allowOutOfZonePunch: false,
    isActive: true,
    sourceRules: Object.fromEntries(
      ATTENDANCE_SOURCE_OVERRIDE_OPTIONS.map((source) => [source, createEmptyAttendanceSourceRuleForm(source, baseCapture)])
    ),
  };
}

export function mapAttendanceConfigToForm(config: AttendanceConfigRecord | null | undefined): AttendanceConfigFormValue {
  if (!config) {
    return createEmptyAttendanceConfigForm();
  }

  const baseCapture = {
    timezone: config.attendanceCapture?.timezone || ATTENDANCE_TIMEZONE_OPTIONS[0],
    requireGeo: config.attendanceCapture?.requireGeo !== false,
    requireFaceEvidence: config.attendanceCapture?.requireFaceEvidence === true,
    duplicateWindowSeconds: config.attendanceCapture?.duplicateWindowSeconds ?? 60,
    allowUntypedEvents: config.attendanceCapture?.allowUntypedEvents !== false,
    normalizeUntypedEvents: config.attendanceCapture?.normalizeUntypedEvents !== false,
    normalizeMistypedEvents: config.attendanceCapture?.normalizeMistypedEvents !== false,
  };

  const form = createEmptyAttendanceConfigForm();
  form.enabled = true;
  form.allowedSources = [...(config.attendanceCapture?.allowedSources || [])];
  form.timezone = baseCapture.timezone;
  form.requireGeo = baseCapture.requireGeo;
  form.requireFaceEvidence = baseCapture.requireFaceEvidence;
  form.duplicateWindowSeconds = baseCapture.duplicateWindowSeconds;
  form.allowUntypedEvents = baseCapture.allowUntypedEvents;
  form.normalizeUntypedEvents = baseCapture.normalizeUntypedEvents;
  form.normalizeMistypedEvents = baseCapture.normalizeMistypedEvents;
  form.trackable = config.tracking?.trackable === true;
  form.trackingIntervalSeconds = normalizeTrackingIntervalSeconds(
    config.tracking?.trackingIntervalSeconds,
    (config.tracking as { trackingIntervalMinutes?: number } | undefined)?.trackingIntervalMinutes
  );
  form.requireOnlineConnection = config.tracking?.requireOnlineConnection !== false;
  form.turnPolicy = {
    enableAutoClose: config.turnPolicy?.enableAutoClose === true,
    autoCloseGraceMinutes: config.turnPolicy?.autoCloseGraceMinutes ?? '',
  };
  form.allowedZoneIds = [...(config.zones?.allowedZoneIds || [])];
  form.allowOutOfZonePunch = config.zones?.allowOutOfZonePunch === true;
  form.isActive = config.isActive !== false;

  for (const source of ATTENDANCE_SOURCE_OVERRIDE_OPTIONS) {
    const rule = config.sourceRules?.[source];
    const hasCustomRule = !!rule && Object.keys(rule).length > 0;
    form.sourceRules[source] = {
      enabled: hasCustomRule,
      timezone: rule?.timezone ?? baseCapture.timezone,
      requireGeo: rule?.requireGeo ?? baseCapture.requireGeo,
      requireFaceEvidence: rule?.requireFaceEvidence ?? baseCapture.requireFaceEvidence,
      duplicateWindowSeconds: rule?.duplicateWindowSeconds ?? baseCapture.duplicateWindowSeconds,
      allowUntypedEvents: rule?.allowUntypedEvents ?? baseCapture.allowUntypedEvents,
      normalizeUntypedEvents: rule?.normalizeUntypedEvents ?? baseCapture.normalizeUntypedEvents,
      normalizeMistypedEvents: rule?.normalizeMistypedEvents ?? baseCapture.normalizeMistypedEvents,
      requireRegisteredDevice: rule?.requireRegisteredDevice === true,
      requireDeviceBinding: rule?.requireDeviceBinding === true,
    };
  }

  return form;
}

export function buildAttendanceConfigPayload(
  form: AttendanceConfigFormValue,
  scope: AttendanceConfigScope
): Record<string, unknown> | null {
  if (!form.enabled) {
    return null;
  }

  const scopeLevel = scope.employeeId
    ? 'employee'
    : scope.orgUnitId
      ? 'orgUnit'
      : scope.companiesRecordId
        ? 'companiesRecord'
        : null;

  if (!scopeLevel) {
    return null;
  }

  const sourceRules = Object.fromEntries(
    ATTENDANCE_SOURCE_OVERRIDE_OPTIONS.map((source) => {
        const rule = form.sourceRules[source];
        if (!form.allowedSources.includes(source) || !rule?.enabled) {
          return [source, {}];
        }

        const payload: AttendanceSourceRuleConfig = {
          ...(usesTimezone(source) ? { timezone: rule.timezone } : {}),
          ...(usesGeo(source) ? { requireGeo: rule.requireGeo } : {}),
          requireFaceEvidence: rule.requireFaceEvidence,
          ...(usesDuplicateWindow(source) ? { duplicateWindowSeconds: Number(rule.duplicateWindowSeconds || 0) } : {}),
          allowUntypedEvents: rule.allowUntypedEvents,
          ...(usesUntypedNormalization(source) ? { normalizeUntypedEvents: rule.normalizeUntypedEvents } : {}),
          ...(usesMistypedNormalization(source) ? { normalizeMistypedEvents: rule.normalizeMistypedEvents } : {}),
          ...(usesDeviceRules(source)
            ? {
                requireRegisteredDevice: rule.requireRegisteredDevice,
                requireDeviceBinding: rule.requireDeviceBinding,
              }
            : {}),
        };
        return [source, payload];
      })
  );

  return {
    scopeLevel,
    ...(scope.companiesRecordId ? { companiesRecordId: scope.companiesRecordId } : {}),
    ...(scope.orgUnitId ? { orgUnitId: scope.orgUnitId } : {}),
    ...(scope.employeeId ? { employeeId: scope.employeeId } : {}),
    attendanceCapture: {
      allowedSources: [...form.allowedSources],
      timezone: form.timezone,
      requireGeo: form.requireGeo,
      requireFaceEvidence: form.requireFaceEvidence,
      duplicateWindowSeconds: Number(form.duplicateWindowSeconds || 0),
      allowUntypedEvents: form.allowUntypedEvents,
      normalizeUntypedEvents: form.normalizeUntypedEvents,
      normalizeMistypedEvents: form.normalizeMistypedEvents,
    },
    tracking: {
      trackable: form.trackable,
      trackingIntervalSeconds: Number(form.trackingIntervalSeconds || 0),
      requireOnlineConnection: form.requireOnlineConnection,
    },
    turnPolicy: {
      enableAutoClose: form.turnPolicy.enableAutoClose,
      ...(form.turnPolicy.enableAutoClose && form.turnPolicy.autoCloseGraceMinutes !== ''
        ? { autoCloseGraceMinutes: Number(form.turnPolicy.autoCloseGraceMinutes) }
        : {}),
    },
    zones: {
      allowedZoneIds: [...form.allowedZoneIds],
      allowOutOfZonePunch: form.allowOutOfZonePunch,
    },
    sourceRules,
    isActive: form.isActive,
  };
}

export function getAttendanceSourceLabelKey(source: string): string {
  return `attendanceConfigsCatalog.sources.${source}`;
}

export function validateAttendanceConfigForm(form: AttendanceConfigFormValue): string | null {
  if (!form.allowedSources.length) {
    return 'attendanceConfigsCatalog.notifications.validationSources';
  }

  if (!form.timezone) {
    return 'attendanceConfigsCatalog.notifications.validationTimezone';
  }

  if (!form.duplicateWindowSeconds || Number(form.duplicateWindowSeconds) <= 0) {
    return 'attendanceConfigsCatalog.notifications.validationDuplicateWindow';
  }

  if (form.trackable && (!form.trackingIntervalSeconds || Number(form.trackingIntervalSeconds) <= 0)) {
    return 'attendanceConfigsCatalog.notifications.validationTrackingInterval';
  }

  if (form.turnPolicy.enableAutoClose && form.turnPolicy.autoCloseGraceMinutes === '') {
    return 'attendanceConfigsCatalog.notifications.validationAutoCloseGrace';
  }

  for (const source of form.allowedSources) {
    const rule = form.sourceRules[source];
    if (!rule?.enabled) {
      continue;
    }

    if (usesTimezone(source) && !rule.timezone) {
      return 'attendanceConfigsCatalog.notifications.validationSourceTimezone';
    }

    if (usesDuplicateWindow(source) && (!rule.duplicateWindowSeconds || Number(rule.duplicateWindowSeconds) <= 0)) {
      return 'attendanceConfigsCatalog.notifications.validationSourceDuplicateWindow';
    }
  }

  return null;
}

export function getAttendanceConfigSourceRuleSummaryKeys(source: string): string[] {
  const keys = ['requireFaceEvidence', 'allowUntypedEvents'];
  if (usesTimezone(source)) {
    keys.unshift('timezone');
  }
  if (usesGeo(source)) {
    keys.push('requireGeo');
  }
  if (usesDuplicateWindow(source)) {
    keys.push('duplicateWindowSeconds');
  }
  if (usesUntypedNormalization(source)) {
    keys.push('normalizeUntypedEvents');
  }
  if (usesMistypedNormalization(source)) {
    keys.push('normalizeMistypedEvents');
  }
  if (usesDeviceRules(source)) {
    keys.push('requireRegisteredDevice', 'requireDeviceBinding');
  }
  return keys;
}

export function usesDeviceRules(source: string): boolean {
  return source === 'device' || source === 'biometricDevice';
}

export function usesTimezone(source: string): boolean {
  return source !== 'biometricDevice';
}

export function usesGeo(source: string): boolean {
  return source !== 'biometricDevice';
}

export function usesDuplicateWindow(source: string): boolean {
  return source !== 'biometricDevice';
}

export function usesUntypedNormalization(source: string): boolean {
  return source !== 'biometricDevice';
}

export function usesMistypedNormalization(source: string): boolean {
  return source !== 'biometricDevice';
}
